import { FF_ROUTE_PRESCREENING_PATIENT_TO_D2D } from '@/constants/featureFlags';
import { GoogleAnalyticsEventName } from '@/constants/types';
import useGoogleTagManager from '@/hooks/useGoogleTagManager';
import { PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY } from '@/pages/PreScreeningForms/PreScreeningForm';
import { formatStringToSha256Hash } from '@/utils/ga-hashing';
import { getSearchParams } from '@/utils/url';
import { useCallback } from 'react';

const ROUTING_EXPERIMENT_ID = FF_ROUTE_PRESCREENING_PATIENT_TO_D2D;

/**
 * Tracking for the direct-to-doctor experiment.
 *
 * Every event ties a browser to its PMS patient by the hashed email, so it is
 * derived once here rather than in each screen.
 */
export const useD2DTracking = () => {
  const { sendGoogleAnalyticsEvent } = useGoogleTagManager();

  const userEmail = getSearchParams('email', {
    localStorageKey: PRESCREENING_FORM_EMAIL_LOCAL_STORAGE_KEY,
  });
  const shaEmail = userEmail ? formatStringToSha256Hash(userEmail) : '';
  const promocode = getSearchParams('promocode');

  const trackExperimentImpression = useCallback(
    (variantId: 'variant' | 'control') => {
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.EXPERIMENT_IMPRESSION, {
        experiment_id: ROUTING_EXPERIMENT_ID,
        variant_id: variantId,
        experiment_platform: 'LaunchDarkly',
        sha_email: shaEmail,
      });
    },
    [sendGoogleAnalyticsEvent, shaEmail],
  );

  const trackTriageStarted = useCallback(() => {
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.TRIAGE_STARTED, { sha_email: shaEmail });
  }, [sendGoogleAnalyticsEvent, shaEmail]);

  const trackTriageCompleted = useCallback(
    (result: 'simple' | 'complex' | null) => {
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.TRIAGE_COMPLETED, {
        result,
        sha_email: shaEmail,
      });
    },
    [sendGoogleAnalyticsEvent, shaEmail],
  );

  const trackD2dQuestionsStarted = useCallback(() => {
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.D2D_QUESTIONS_STARTED, { sha_email: shaEmail });
  }, [sendGoogleAnalyticsEvent, shaEmail]);

  const trackD2dQuestionsCompleted = useCallback(() => {
    sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.D2D_QUESTIONS_COMPLETED, { sha_email: shaEmail });
  }, [sendGoogleAnalyticsEvent, shaEmail]);

  const trackImmediateD2DOffered = useCallback(
    (offered: boolean) => {
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.IMMEDIATE_D2D_OFFERED, {
        offered: String(offered),
        sha_email: shaEmail,
      });
    },
    [sendGoogleAnalyticsEvent, shaEmail],
  );

  const trackStartBooking = useCallback(
    (eventLabel: 'Immediate D2D Consultation' | 'Initial D2D Consultation') => {
      sendGoogleAnalyticsEvent(GoogleAnalyticsEventName.START_BOOKING, {
        event_category: 'cal.com',
        event_label: eventLabel,
        sha_email: shaEmail,
        ...(promocode ? { promocode } : {}),
      });
    },
    [sendGoogleAnalyticsEvent, shaEmail, promocode],
  );

  return {
    trackExperimentImpression,
    trackTriageStarted,
    trackTriageCompleted,
    trackD2dQuestionsStarted,
    trackD2dQuestionsCompleted,
    trackImmediateD2DOffered,
    trackStartBooking,
  };
};
